// AdminReviewsTab — moderation queue for product/service reviews.
//
// Loads /api/admin/reviews?status=…, lets the admin approve / reject / delete.
// Approved reviews become publicly visible on the matching detail page;
// rejected reviews stay in the DB for audit but never surface.
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance, getApiErrorMessage } from './_shared';

const STATUS_TABS = [
  { id: 'pending',  label: 'Pending'  },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-0.5 text-sm">
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          className={n <= rating ? 'ph-fill ph-star text-[#F4B400]' : 'ph ph-star text-gray-300'}
        />
      ))}
    </div>
  );
}

export default function AdminReviewsTab() {
  const [status, setStatus] = useState('pending');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/admin/reviews', {
        params: { status, q: query || undefined },
      });
      setReviews(r.data || []);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load reviews'));
    } finally {
      setLoading(false);
    }
  }, [status, query]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (id, action) => {
    setBusyId(id);
    try {
      if (action === 'delete') {
        await axiosInstance.delete(`/admin/reviews/${id}`);
        toast.success('Review deleted');
      } else {
        await axiosInstance.post(`/admin/reviews/${id}/${action}`);
        toast.success(`Review ${action}d`);
      }
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, `Failed to ${action} review`));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-reviews-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setStatus(t.id)}
            data-testid={`admin-reviews-status-${t.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wide ${
              status === t.id
                ? 'bg-[#2B3A67] text-white'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            {t.label}
          </button>
        ))}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search email / product…"
          data-testid="admin-reviews-search"
          className="ml-auto px-3 py-1.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B3A67]/30 w-56"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm" data-testid="admin-reviews-empty">
          No {status} reviews.
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
              data-testid={`admin-review-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-extrabold text-sm truncate">{r.user_name || r.user_email}</p>
                    <span className="text-xs text-gray-400">·</span>
                    <p className="text-xs text-gray-500 truncate">{r.user_email}</p>
                    {r.verified_buyer && (
                      <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
                        <i className="ph-bold ph-seal-check" />
                        Verified buyer
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {r.product_kind === 'service' ? 'Service' : 'Product'} ·{' '}
                    <span className="font-bold text-gray-600">{r.product_name}</span>
                  </p>
                  <div className="mt-2"><Stars rating={r.rating} /></div>
                  {r.text && (
                    <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap break-words">
                      {r.text}
                    </p>
                  )}
                  <p className="text-[10px] text-gray-400 mt-2">
                    {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {r.status !== 'approved' && (
                    <button
                      onClick={() => moderate(r.id, 'approve')}
                      disabled={busyId === r.id}
                      data-testid={`admin-review-approve-${r.id}`}
                      className="px-3 py-1.5 rounded-xl bg-emerald-500 text-white text-xs font-extrabold disabled:opacity-60"
                    >
                      Approve
                    </button>
                  )}
                  {r.status !== 'rejected' && (
                    <button
                      onClick={() => moderate(r.id, 'reject')}
                      disabled={busyId === r.id}
                      data-testid={`admin-review-reject-${r.id}`}
                      className="px-3 py-1.5 rounded-xl border-2 border-amber-400 text-amber-700 text-xs font-extrabold disabled:opacity-60"
                    >
                      Reject
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (window.confirm('Permanently delete this review?')) moderate(r.id, 'delete');
                    }}
                    disabled={busyId === r.id}
                    data-testid={`admin-review-delete-${r.id}`}
                    className="px-3 py-1.5 rounded-xl border-2 border-red-300 text-red-500 text-xs font-extrabold disabled:opacity-60"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
